import {APP_INITIALIZER, enableProdMode, importProvidersFrom} from '@angular/core';
import {environment} from './environments/environment';
import {AppComponent} from './app.component';
import {CookieModule} from 'ngx-cookie';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {provideAnimations} from '@angular/platform-browser/animations';
import {ModalModule} from 'ngx-bootstrap/modal';
import {LaddaModule} from './libraries/angular2-ladda/ladda.module';
import {FileUploadModule} from 'ng2-file-upload';
import {FormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {provideCharts, withDefaultRegisterables} from 'ng2-charts';
import {NgSelectModule} from '@ng-select/ng-select';
import {NgIdleModule} from '@ng-idle/core';
import {NgIdleKeepaliveModule} from '@ng-idle/keepalive';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {routes} from './routes';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {ToasterModule} from 'angular2-toaster';
import {provideRouter} from '@angular/router';
import {TreeviewModule} from 'ngx-treeview';
import {InitializationAndConfigurationService} from './services/configuration/initializationAndConfiguration.service';
import {AdminService} from './services/admin.service';
import {UserService} from './services/user.service';
import {ApplicationService} from './services/application.service';
import {ToDoItemService} from './services/toDoItem.service';
import {DateHelperService} from './services/dateHelper.service';
import {TicklerService} from './services/tickler.service';
import {HttpErrorsService} from './services/support/http-errors.service';
import {DefaultValuesService} from './services/defaultValues.service';
import {FilterUtilityService} from './services/support/filter-utility.service';
import {ClientService} from './services/client.service';
import {BrowserDetect} from './services/support/browser-detect.service';
import {LocalStorageService} from './services/support/local-storage.service';
import {DocumentService} from './services/documents/document.service';
import {EnrollmentService} from './services/enrollments/enrollment.service';
import {ExternalContactService} from './services/externalContact/externalContact.service';
import {FormService} from './services/forms/form.service';
import {AssessmentService} from './services/assessments/assessment.service';
import {AssessmentsGroupService} from './services/assessments/assessmentsGroup.service';
import {InstrumentService} from './services/instrument.service';
import {NarrativeService} from './services/plans/narrative.service';
import {DashboardService} from './services/dashboard/dashboard.service';
import {ChecklistService} from './services/checklists/checklist.service';
import {EventBusService} from './services/support/event-bus.service';
import {PdfPrintService} from './services/pdf-print.service';
import {AdjunctiveAssessmentService} from './services/assessments/adjunctiveAssessment.service';
import {ActionPlanningTableService} from './services/assessments/actionPlanningTableService.service';
import {PlanService} from './services/plans/plan.service';
import {NarrativeStylingService} from './services/plans/narrativeStyling.service';
import {ConfigurableStylingService} from './services/support/configurable-styling.service';
import {CertificationService} from './services/certification.service';
import {AlgorithmService} from './services/algorithm.service';
import {DashboardCacheService} from './services/dashboard/dashboard-cache.service';
import {ReportsService} from './services/reports.service';
import {SupervisorDashboardService} from './services/dashboard/supervisor-dashboard.service';
import {UrlService} from './services/url.service';
import {UnauthorizedInterceptor} from './components/interceptors/unauthorized.interceptor';
import {RequestAuthorizationInterceptor} from "./components/interceptors/request-authorization.interceptor";

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(BrowserModule, BsDropdownModule.forRoot(), TreeviewModule.forRoot(),
            TabsModule.forRoot(), NgIdleModule.forRoot(), NgIdleKeepaliveModule.forRoot(), NgSelectModule,
            FormsModule, ToasterModule.forRoot(), FileUploadModule, LaddaModule, ModalModule.forRoot(),
            TooltipModule.forRoot(), CookieModule.withOptions()),
        provideCharts(withDefaultRegisterables()),

        UrlService,
        InitializationAndConfigurationService,
        DefaultValuesService,
        HttpErrorsService,
        DateHelperService,
        FilterUtilityService,
        LocalStorageService,
        EventBusService,
        BrowserDetect,

        // Page Services
        ActionPlanningTableService,
        AdminService,
        AdjunctiveAssessmentService,
        AlgorithmService,
        ApplicationService,
        AssessmentService,
        AssessmentsGroupService,
        CertificationService,
        ChecklistService,
        ClientService,
        DashboardService,
        DashboardCacheService,
        DocumentService,
        EnrollmentService,
        ExternalContactService,
        FormService,
        InstrumentService,
        NarrativeService,
        PlanService,
        PdfPrintService,
        ReportsService,
        ToDoItemService,
        TicklerService,
        UserService,
        NarrativeStylingService,
        ConfigurableStylingService,
        SupervisorDashboardService,
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializationFactory,
            deps: [InitializationAndConfigurationService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestAuthorizationInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        provideRouter(routes)
    ]
});

export function appInitializationFactory(initializationAndConfigurationService: InitializationAndConfigurationService) {
    return () => initializationAndConfigurationService.initializeApplication();
}
